import { useCallback, useMemo, useState } from 'react';
import { decodeJwt } from 'jose';
import { config } from '../config/runtimeConfig';

const ONE_MINUTE_IN_MS = 60 * 1000;

/**
 * Определяет нужно ли использовать b2b-api для получения connection details
 * Если есть FRONTEND_TOKEN - используем b2b-api, иначе - LiveKit Cloud API напрямую
 */
const shouldUseB2bApi = () => {
  const token = config.FRONTEND_TOKEN;
  return typeof token === 'string' && token.trim().length > 0;
};

/**
 * Получить endpoint для connection details
 * Если есть frontend token - используем b2b-api, иначе - LiveKit Cloud API
 */
const getEndpoint = () => {
  if (shouldUseB2bApi()) {
    const baseUrl = config.API_BASE_URL;
    if (!baseUrl || typeof baseUrl !== 'string' || baseUrl.trim().length === 0) {
      console.error('[useLiveKitConnection] API_BASE_URL not configured but FRONTEND_TOKEN is set');
      throw new Error('API_BASE_URL is required when using FRONTEND_TOKEN');
    }
    // b2b-api endpoint для публичного доступа через frontend token
    // Добавляем /api префикс если его нет в baseUrl
    const apiPath = baseUrl.endsWith('/api') ? '' : '/api';
    return `${baseUrl}${apiPath}/livekit/public/connection-details`;
  }
  // Fallback: прямой вызов LiveKit Cloud API (legacy)
  return config.LIVEKIT_CONN_DETAILS_ENDPOINT;
};

const getSandboxId = () => {
  // return import.meta.env.VITE_LIVEKIT_SANDBOX_ID || 'aiuc-sg-demo-1vgluw';
  return 'aiuc-sg-demo-1vgluw';
};

const getAgentName = () => {
  // Приоритет: config.AGENT_ID (из URL или env) > hostname detection
  if (config.AGENT_ID) {
    console.info(`[getAgentName] Using agentName from config:`, config.AGENT_ID);
    return config.AGENT_ID;
  }

  // Fallback: определение по hostname
  let agentName = 'prod-agent';
  const hostName = typeof window !== 'undefined' ? window.location.hostname : '';

  const envName = config.ENVIRONMENT_NAME;
  if (envName === 'staging' || envName === 'test') {
    agentName = 'test-agent';
  }
  console.info(`[getAgentName] hostName: ${hostName}; agentName: ${agentName}`);
  return agentName;
};

export const isTokenExpired = (token) => {
  if (!token) {
    return true;
  }

  try {
    const payload = decodeJwt(token);
    if (!payload?.exp) {
      return true;
    }

    const expiresAt = payload.exp * 1000 - ONE_MINUTE_IN_MS;
    return Date.now() > expiresAt;
  } catch (error) {
    console.error('[useLiveKitConnection] Failed to decode token:', error);
    return true;
  }
};

const useLiveKitConnection = () => {
  const [connectionDetails, setConnectionDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const useB2bApi = useMemo(() => shouldUseB2bApi(), []);
  const endpoint = useMemo(() => getEndpoint(), []);
  const sandboxId = useMemo(() => getSandboxId(), []);
  const agentName = useMemo(() => getAgentName(), []);

  const fetchConnectionDetails = useCallback(async (metadata = {}) => {
    console.warn('[566-DEBUG] fetchConnectionDetails: starting fetch');
    setIsLoading(true);
    setError(null);

    try {
      const url = new URL(endpoint, window.location.origin);

      let headers;
      let body;

      if (useB2bApi) {
        // b2b-api: используем frontend token для авторизации
        // API вернет: { sessionId, url, token, serverUrl, agentName, sandboxId }
        headers = {
          'Content-Type': 'application/json',
          'X-Frontend-Token': config.FRONTEND_TOKEN,
        };

        // Деструктурируем уже использованные поля чтобы избежать дублирования
        const {
          sessionId: browserSessionId,
          currentDate,
          browserControlEnabled,
          skipDispatch,
          ...restMetadata
        } = metadata;

        // Payload для b2b-api (LiveKitPublicConnectionRequestDto)
        const payload = {
          // agentId можно передать если нужен конкретный агент
          // metadata передается на сервер и объединяется с серверной metadata
          // Issue #717: skipDispatch prevents duplicate agent dispatch on reconnect
          ...(skipDispatch && { skipDispatch: true }),
          metadata: {
            // Сначала остальная metadata
            ...restMetadata,
            // Затем явные поля (перезаписывают если были в restMetadata)
            browserSessionId, // sessionId браузера для browser control
            currentDate,
            browserControlEnabled,
            inventoryId: config.INVENTORY_ID,
          },
        };

        body = JSON.stringify(payload);

        if (import.meta.env.DEV) {
          console.debug('[useLiveKitConnection] Using b2b-api endpoint:', endpoint);
        }
      } else {
        // Legacy: прямой вызов LiveKit Cloud API
        headers = {
          'Content-Type': 'application/json',
          'X-Sandbox-Id': sandboxId,
        };

        const payload = {
          ...(agentName && {
            room_config: {
              agents: [{ agent_name: agentName }],
            },
          }),
          ...(Object.keys(metadata).length > 0 && { metadata }),
        };

        body = Object.keys(payload).length > 0 ? JSON.stringify(payload) : '{}';

        if (import.meta.env.DEV) {
          console.debug('[useLiveKitConnection] Using LiveKit Cloud API (legacy):', endpoint);
        }
      }

      console.warn('[566-DEBUG] fetchConnectionDetails: POST', url.toString(), { useB2bApi });

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body,
      });

      console.warn('[566-DEBUG] fetchConnectionDetails: response status', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch connection details (${response.status}): ${errorText}`);
      }

      const data = await response.json();

      // Нормализуем ответ для совместимости
      // b2b-api возвращает: { sessionId, url, token, serverUrl, agentName, sandboxId }
      // LiveKit Cloud API возвращает: { serverUrl, participantToken, roomName, ... }
      const normalizedData = useB2bApi
        ? {
            serverUrl: data.serverUrl,
            participantToken: data.token,
            roomName: data.sandboxId,
            sessionId: data.sessionId,
            // Сохраняем оригинальные данные
            ...data,
          }
        : data;

      setConnectionDetails(normalizedData);
      console.warn('[566-DEBUG] fetchConnectionDetails: SUCCESS', {
        serverUrl: normalizedData.serverUrl,
        hasToken: !!normalizedData.participantToken,
        tokenLength: normalizedData.participantToken?.length,
        sessionId: normalizedData.sessionId,
        roomName: normalizedData.roomName,
      });
      if (import.meta.env.DEV) {
        console.debug('[useLiveKitConnection] Connection details received:', {
          serverUrl: normalizedData.serverUrl,
          sessionId: normalizedData.sessionId,
          roomName: normalizedData.roomName || normalizedData.sandboxId,
        });
      }

      return normalizedData;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown LiveKit connection error');
      console.warn('[566-DEBUG] fetchConnectionDetails: ERROR', errorObj.message);
      // Ошибки логируем всегда (не только в dev)
      console.error('[useLiveKitConnection] Error:', errorObj.message);
      setError(errorObj);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, sandboxId, agentName, useB2bApi]);

  const ensureConnectionDetails = useCallback(async (metadata = {}) => {
    console.warn('[566-DEBUG] ensureConnectionDetails() ENTER', {
      hasConnectionDetails: !!connectionDetails,
      tokenExpired: connectionDetails ? isTokenExpired(connectionDetails.participantToken) : 'N/A',
      metadataSessionId: metadata.sessionId?.substring(0, 8),
      currentSessionId: connectionDetails?.sessionId?.substring(0, 8),
    });

    // Если нет connection details - запрашиваем
    if (!connectionDetails) {
      console.warn('[566-DEBUG] ensureConnectionDetails: no cached details → FETCHING');
      return fetchConnectionDetails(metadata);
    }

    // Если токен истёк - запрашиваем
    if (isTokenExpired(connectionDetails.participantToken)) {
      console.warn('[566-DEBUG] ensureConnectionDetails: token expired → FETCHING');
      return fetchConnectionDetails(metadata);
    }

    // ВАЖНО: Если sessionId в metadata отличается от текущего в connectionDetails,
    // нужно запросить новые connection details для корректной синхронизации
    // с browserControlWS
    const metadataSessionId = metadata.sessionId;
    const currentSessionId = connectionDetails.sessionId;
    if (metadataSessionId && currentSessionId && metadataSessionId !== currentSessionId) {
      console.warn('[566-DEBUG] ensureConnectionDetails: sessionId changed → FETCHING', {
        current: currentSessionId?.substring(0, 8),
        new: metadataSessionId?.substring(0, 8),
      });
      return fetchConnectionDetails(metadata);
    }

    console.warn('[566-DEBUG] ensureConnectionDetails: using CACHED details');
    return connectionDetails;
  }, [connectionDetails, fetchConnectionDetails]);

  /**
   * Issue #510: Clear connection details to force fresh token on next connect
   * This must be called when LiveKit room is disconnected to prevent
   * stale connection details from being reused on reconnect
   */
  const clearConnectionDetails = useCallback(() => {
    console.warn('[566-DEBUG] clearConnectionDetails() called, caller:', new Error().stack?.split('\n')[2]?.trim());
    setConnectionDetails(null);
    setError(null);
    if (import.meta.env.DEV) {
      console.debug('[useLiveKitConnection] Connection details cleared');
    }
  }, []);

  return {
    connectionDetails,
    isLoading,
    error,
    ensureConnectionDetails,
    refreshConnectionDetails: fetchConnectionDetails,
    clearConnectionDetails,
  };
};

export default useLiveKitConnection;
